import { Component, inject, OnInit, AfterViewInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LoaderService } from '../../services/loader/loader.service';
import { WorkOrderService } from '../../services/work-order/work-order.service';
import { UtilsService } from '../../services/utils/utils.service';
import { UserPreferencesService } from '../../services/user-preferences/user-preferences.service';
import {
  debounceTime,
  distinctUntilChanged,
  firstValueFrom,
  lastValueFrom,
  Subscription,
  tap,
} from 'rxjs';
import { GetAssignUserDetails } from '../../models/work-order.model';
import { LocalStorageService } from '../../services/local-storage/localstorage.service';
import { ToastService } from '../../services/toast/toast.service';

@Component({
  selector: 'app-reassign-wo-popup',
  standalone: false,

  templateUrl: './reassign-wo-popup.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './reassign-wo-popup.component.scss',
})
export class ReassignWoPopupComponent implements OnInit, AfterViewInit, OnDestroy {
  dialogData = inject(MAT_DIALOG_DATA);
  private fb = inject(FormBuilder);
  private loaderService = inject(LoaderService);
  private workOrderService = inject(WorkOrderService);
  private utilsService = inject(UtilsService);
  private userPreferencesService = inject(UserPreferencesService);
  private dialogRef = inject<MatDialogRef<ReassignWoPopupComponent>>(MatDialogRef);
  private localstorageService = inject(LocalStorageService);
  private toastService = inject(ToastService);

  reassignWoForm: FormGroup;
  assignedUserList: any[] = [];
  allAssignedUserList: any[] = [];
  partnerCompanyList: any[] = [];
  allPartnerCompanyOptions: any = [];
  allUserList: any;
  selectedUserPartnerCmpId: any;
  userPreferencesData: any;
  technicianSearchControl: UntypedFormControl;
  partnerCmpSearchControl: UntypedFormControl;
  subs = new Subscription();
  isNotAssign: boolean;
  woTypeList: any;
  skillList: string[] = [];
  displayUserNotFoundMsg = false;

  async ngOnInit() {
    this.createForm();
    this.loadUserPreferenceData();
    this.refreshCompanyAndProjectDetails();
    await this.getAssignToList(this.dialogData?.woTypeList || []);
    this.getPartnerCompanyList();

    if (this.dialogData) {
      this.isNotAssign = this.dialogData?.workOrderDataArray?.some(
        (woData: any) => woData.technician == ''
      );
      this.woTypeList = this.dialogData?.woTypeList;
    }
  }

  ngAfterViewInit(): void {
    this.handleSearchEvent();
  }

  skillDisplayText(): string {
    // woTypeList is an array of objects, each may have skillSetDetails (array of skills)
    if (!Array.isArray(this.woTypeList)) return '';
    const allSkills: string[] = [];
    if (this.woTypeList.length > 0) {
      this.woTypeList?.forEach(item => {
        if (Array.isArray(item?.skillSetDetails)) {
          item?.skillSetDetails?.forEach((skillObj: any) => {
            if (skillObj?.skill) {
              allSkills.push(skillObj?.skill);
            }
          });
        }
      });
    }
    // Remove duplicates
    this.skillList = Array.from(new Set(allSkills));
    return this.skillList.join(', ');
  }

  createForm() {
    this.reassignWoForm = this.fb.group({
      techId: [0, Validators.required],
      partnerCompanyId: [0, Validators.required],
      assignLinkedWo: [true],
    });
    this.technicianSearchControl = new UntypedFormControl('');
    this.partnerCmpSearchControl = new UntypedFormControl('');
  }

  loadUserPreferenceData() {
    this.userPreferencesData =
      this.userPreferencesService.getUserPreferencesData();
  }

  refreshCompanyAndProjectDetails() {
    // subscription for refresh project details based on company selection
    this.subs.add(
      this.utilsService.refreshCompanyAndProjectDetails.subscribe((data) => {
        if (data) {
          this.loadUserPreferenceData();
        }
      })
    );
  }

  isDisabledBtn() {
    const { techId, partnerCompanyId } = this.reassignWoForm.value;
    return !techId && !partnerCompanyId;
  }

  handleSearchEvent(): void {
    this.subs.add(
      this.technicianSearchControl.valueChanges
        .pipe(
          debounceTime(300),
          distinctUntilChanged((curr, prev) => curr === prev),
          tap((search: string) => {
            this.assignedUserList = this.allAssignedUserList.filter((team) =>
              team.name.toLowerCase().includes(search.toLowerCase())
            );
            if (!search) {
              this.assignedUserList = [...this.allAssignedUserList];
            }
          })
        )
        .subscribe()
    );

    this.subs.add(
      this.partnerCmpSearchControl.valueChanges
        .pipe(
          debounceTime(300),
          distinctUntilChanged((curr, prev) => curr === prev),
          tap((search: string) => {
            this.partnerCompanyList = this.allPartnerCompanyOptions.filter(
              (company) =>
                company.name.toLowerCase().includes(search.toLowerCase())
            );
            if (!search) {
              this.partnerCompanyList = [...this.allPartnerCompanyOptions];
            }
          })
        )
        .subscribe()
    );
  }

  async getAssignToList(woTypeIds: any[] = []): Promise<void> {
    if (woTypeIds.length === 0) return;
    this.loaderService.showLoader();
    const selectedWoTypeIds = woTypeIds?.map((row) => row?.id).join(',');
    const payload: GetAssignUserDetails = {
      companyId: this.userPreferencesData?.selectedCompanyId || 0,
      workOrderTypeId: selectedWoTypeIds || '',
      partnerCompanyId:
        this.localstorageService.getItem('partnerCompanyId') || 0,
    };

    try {
      const assignUserList = await firstValueFrom(
        this.workOrderService.getAssignUserDetails(payload)
      );
      this.allUserList = assignUserList;
      if (assignUserList) {
        //
        //  const skillSetDetails = this.dialogData?.skillSetDetails || [];
        //  let filteredUsers;
        //  if (skillSetDetails.length > 0) {
        //    const requiredSkillIds = skillSetDetails.map(skill => skill?.skillId);

        //    filteredUsers = assignUserList.filter((user) => {
        //      const userSkillIds = user?.skillset.map(skill => skill.skillId) || [];
        //      return requiredSkillIds.some(skillId => userSkillIds.includes(skillId)
        //      );
        //    });
        //  } else {
        //    filteredUsers = assignUserList;
        //  }
        this.assignedUserList = assignUserList;
        this.allAssignedUserList = assignUserList;
        this.displayUserNotFoundMsg = true;
      }
      this.loaderService.hideLoader();
    } catch (err) {
      this.loaderService.hideLoader();
      console.error('Error while fetching assign user list:', err);
    }
  }

  async getPartnerCompanyList() {
    this.allPartnerCompanyOptions = [];
    if (
      !this.userPreferencesData?.userId ||
      !this.userPreferencesData?.selectedCompanyId
    ) {
      console.error('Params missing!');
      return;
    }

    this.loaderService.showLoader();
    try {
      const payload = {
        id: 0,
        companyId: this.userPreferencesData?.selectedCompanyId,
      };
      this.partnerCompanyList = await lastValueFrom(
        this.workOrderService.getPartnerCompanyList(payload)
      );
      this.partnerCompanyList = this.partnerCompanyList.filter((data) =>
        this.assignedUserList.some((user) => user.partnerCompanyId === data.id)
      );
      this.allPartnerCompanyOptions = [...this.partnerCompanyList];
      this.loaderService.hideLoader();
    } catch (error) {
      this.loaderService.hideLoader();
      console.error(error);
    }
  }

  assignToChange() {
    const formValue = this.reassignWoForm.value;
    const selectedUserDtls = this.allUserList?.find(
      (user) => user.id === formValue.techId
    )?.userCompanyDtls;
    this.selectedUserPartnerCmpId =
      selectedUserDtls?.find(
        (cmpDetails: any) => cmpDetails.partnerCompanyId > 0
      )?.partnerCompanyId ?? 0;
    this.reassignWoForm.patchValue({
      partnerCompanyId: this.selectedUserPartnerCmpId,
    });
    this.changeUserDetails(this.selectedUserPartnerCmpId);
  }

  onPartnerCmpChange(event: any) {
    this.changeUserDetails(event.value);
    this.reassignWoForm.patchValue({ techId: +0 });
  }

  changeUserDetails(partnerCmpId: number) {
    if (partnerCmpId == 0) {
      this.assignedUserList = this.allUserList;
    } else {
      this.assignedUserList = this.allUserList?.filter((userData: any) =>
        userData?.userCompanyDtls?.some(
          (companyDetails: any) =>
            companyDetails['partnerCompanyId'] === partnerCmpId
        )
      );
    }
    this.allAssignedUserList = this.assignedUserList || [];
    this.selectedUserPartnerCmpId = 0;
  }

  toggleLinkWO(event: any) {
    if (event.checked) {
      this.getWorkOrderTypeList();
    } else {
      this.getAssignToList(this.dialogData?.woTypeList || []);
      this.woTypeList = this.dialogData.woTypeList || [];
    }
  }

  async getWorkOrderTypeList() {
    const woIds: any[] =
      this.dialogData?.workOrderDataArray?.map((data: any) => data.id) || [];
    let childSkillData: any[] = [];
    this.loaderService.showLoader();
    try {
      if (woIds.length) {
        childSkillData = await firstValueFrom(
          this.workOrderService.getChildWOSkillSetDetails(woIds)
        );
      }

      if (childSkillData.length > 0) {
        const allWoData = [
          ...(this.dialogData?.woTypeList || []),
          ...childSkillData,
        ];
        this.getAssignToList(allWoData);

        childSkillData = childSkillData.map((childData: any) => {
          const { woTypeSkillSetDtls, ...otherChildDtls } = childData;
          if (woTypeSkillSetDtls) {
            return {
              ...otherChildDtls,
              skillSetDetails: woTypeSkillSetDtls,
            };
          }
          return otherChildDtls;
        });

        this.woTypeList = [...(this.woTypeList || []), ...childSkillData];
      }
      this.loaderService.hideLoader();
    } catch (error) {
      console.error(
        'Error fetching child work order skill set details:',
        error
      );
      this.loaderService.hideLoader();
    }
  }

  onSubmit() {
    if (this.isDisabledBtn()) {
      this.toastService.showWarning('Select a technician or a partner company before submitting.');
      return;
    }
    const data = this.reassignWoForm.value;
    this.dialogRef.close({ formData: data, closeType: 'submit' });
  }

  ngOnDestroy() {
    this.utilsService.refreshCompanyAndProjectDetails.next(false);
    this.subs.unsubscribe();
  }
}
